<template>
    <div class="flex flex-col gap-4">
        <p class="body-small text-valence-grey-750">Help Nadia understand why this goal doesn't fit.</p>
        <div class="flex flex-col gap-2">
            <label v-for="option in issueOptions" :key="option.value" :for="`goal-issue-${option.value}`" class="flex cursor-pointer items-center gap-2">
                <input
                    :id="`goal-issue-${option.value}`"
                    v-model="selectedIssues"
                    type="checkbox"
                    :value="option.value"
                    class="rounded border-valence-grey-300"
                    :disabled="isProcessing" />
                <span class="body-small text-valence-grey-850">{{ option.label }}</span>
            </label>
        </div>
        <textarea
            v-model="comment"
            aria-label="goal-feedback-additional-comments"
            rows="3"
            placeholder="Any other comments?"
            :disabled="isProcessing"
            class="resize-none rounded-lg border border-valence-grey-250 px-3 py-2.5 body-small text-valence-grey-850 placeholder:text-valence-grey-400 focus:outline-none focus:ring-1 focus:ring-valence-primary-500 disabled:opacity-50" />
        <div class="flex items-center justify-end gap-3 pt-2">
            <button type="button" class="body-small text-valence-primary-500 disabled:opacity-50" :disabled="isProcessing" @click="emit('cancel')">Cancel</button>
            <button
                type="button"
                class="rounded-full bg-valence-primary-500 px-3.5 py-1 body-small text-white hover:bg-valence-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                :disabled="isProcessing"
                @click="handleSubmit">
                <i v-if="isProcessing" class="bi bi-arrow-clockwise animate-spin" aria-hidden="true" />
                {{ isProcessing ? "Discarding…" : "Discard goal" }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { GoalFeedbackRequest } from "~vue/types/goals";
import { computed, ref } from "vue";

const props = defineProps<{ goalId: string }>();
const emit = defineEmits<{ discarded: []; cancel: [] }>();

const isProcessing = ref(false);
const selectedIssues = ref<string[]>([]);
const comment = ref("");

const issueOptions = [
    { value: "not_relevant", label: "This goal is not relevant to me" },
    { value: "deprioritized", label: "This goal is no longer a focus" },
    { value: "not_a_goal", label: "This is a task, not a goal" },
];

const hasFeedback = computed(() => selectedIssues.value.length > 0 || comment.value.trim() !== "");

const feedbackBody = computed<GoalFeedbackRequest>(() => ({
    value: -1,
    issues: selectedIssues.value,
    comment: comment.value.trim() || null,
}));

const { execute: submitFeedback } = useFetch(
    computed(() => `/v1/goals/${props.goalId}/feedback`),
    { immediate: false },
)
    .post(feedbackBody)
    .json();

function reset() {
    selectedIssues.value = [];
    comment.value = "";
}

async function handleSubmit() {
    isProcessing.value = true;
    if (hasFeedback.value) {
        await submitFeedback();
    }
    isProcessing.value = false;
    reset();
    emit("discarded");
}
</script>
